'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── DSP Catalog (150+ platforms) ────────────────────────────────────────────

const DSP_CATALOG = [
  // Tier 1 — Global Giants
  { id: 'spotify',       name: 'Spotify',           region: 'Global',    color: '#1DB954', tier: 1 },
  { id: 'apple_music',   name: 'Apple Music',        region: 'Global',    color: '#FC3C44', tier: 1 },
  { id: 'youtube_music', name: 'YouTube Music',      region: 'Global',    color: '#FF0000', tier: 1 },
  { id: 'amazon_music',  name: 'Amazon Music',       region: 'Global',    color: '#00A8E1', tier: 1 },
  { id: 'tidal',         name: 'TIDAL',              region: 'Global',    color: '#00FFFF', tier: 1 },
  { id: 'deezer',        name: 'Deezer',             region: 'Global',    color: '#A238FF', tier: 1 },
  { id: 'pandora',       name: 'Pandora',            region: 'US',        color: '#005483', tier: 1 },
  { id: 'soundcloud',    name: 'SoundCloud',         region: 'Global',    color: '#FF5500', tier: 1 },
  // Tier 2 — India
  { id: 'jiosaavn',      name: 'JioSaavn',           region: 'India',     color: '#2BC5B4', tier: 2 },
  { id: 'gaana',         name: 'Gaana',              region: 'India',     color: '#E72C30', tier: 2 },
  { id: 'wynk',          name: 'Wynk Music',         region: 'India',     color: '#7B2FBE', tier: 2 },
  { id: 'hungama',       name: 'Hungama',            region: 'India',     color: '#FF6B00', tier: 2 },
  { id: 'resso',         name: 'Resso',              region: 'India',     color: '#FF2D55', tier: 2 },
  // Tier 2 — Asia Pacific
  { id: 'kkbox',         name: 'KKBOX',              region: 'Asia',      color: '#009944', tier: 2 },
  { id: 'joox',          name: 'JOOX',               region: 'Asia',      color: '#1DB954', tier: 2 },
  { id: 'melon',         name: 'Melon',              region: 'Korea',     color: '#00C73C', tier: 2 },
  { id: 'bugs',          name: 'Bugs',               region: 'Korea',     color: '#FF4500', tier: 2 },
  { id: 'genie',         name: 'Genie Music',        region: 'Korea',     color: '#0066CC', tier: 2 },
  { id: 'flo',           name: 'FLO',                region: 'Korea',     color: '#FF6B35', tier: 2 },
  { id: 'netease',       name: 'NetEase Cloud',      region: 'China',     color: '#C20C0C', tier: 2 },
  { id: 'qq_music',      name: 'QQ Music',           region: 'China',     color: '#FFCD00', tier: 2 },
  { id: 'kugou',         name: 'Kugou Music',        region: 'China',     color: '#1A73E8', tier: 2 },
  { id: 'kuwo',          name: 'Kuwo Music',         region: 'China',     color: '#FF6600', tier: 2 },
  { id: 'anghami',       name: 'Anghami',            region: 'MENA',      color: '#6B4FBB', tier: 2 },
  { id: 'boomplay',      name: 'Boomplay',           region: 'Africa',    color: '#FF4500', tier: 2 },
  { id: 'audiomack',     name: 'Audiomack',          region: 'Global',    color: '#FFA500', tier: 2 },
  // Tier 3 — Niche / Regional
  { id: 'napster',       name: 'Napster',            region: 'Global',    color: '#0066FF', tier: 3 },
  { id: 'iheartradio',   name: 'iHeartRadio',        region: 'US',        color: '#C6002B', tier: 3 },
  { id: 'qobuz',         name: 'Qobuz',              region: 'Europe',    color: '#0066CC', tier: 3 },
  { id: 'tencent_music', name: 'Tencent Music',      region: 'China',     color: '#1DB954', tier: 3 },
  { id: 'yandex_music',  name: 'Yandex Music',       region: 'Russia',    color: '#FFCC00', tier: 3 },
  { id: 'zvuk',          name: 'Zvuk',               region: 'Russia',    color: '#FF6B35', tier: 3 },
  { id: 'claromusica',   name: 'Claro Música',       region: 'LatAm',     color: '#E30613', tier: 3 },
  { id: 'spotify_podcast', name: 'Spotify Podcast',  region: 'Global',    color: '#1DB954', tier: 3 },
  { id: 'apple_podcast', name: 'Apple Podcasts',     region: 'Global',    color: '#9933CC', tier: 3 },
  { id: 'amazon_podcast', name: 'Amazon Podcasts',   region: 'Global',    color: '#00A8E1', tier: 3 },
  { id: 'shazam',        name: 'Shazam',             region: 'Global',    color: '#0088FF', tier: 3 },
  { id: 'beatport',      name: 'Beatport',           region: 'Global',    color: '#00FF00', tier: 3 },
  { id: 'traxsource',    name: 'Traxsource',         region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'bandcamp',      name: 'Bandcamp',           region: 'Global',    color: '#1DA0C3', tier: 3 },
  { id: 'mixcloud',      name: 'Mixcloud',           region: 'Global',    color: '#5000FF', tier: 3 },
  { id: '7digital',      name: '7digital',           region: 'Global',    color: '#FF0066', tier: 3 },
  { id: 'medianet',      name: 'MediaNet',           region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'gracenote',     name: 'Gracenote',          region: 'Global',    color: '#666666', tier: 3 },
  { id: 'musicbrainz',   name: 'MusicBrainz',        region: 'Global',    color: '#BA478F', tier: 3 },
  { id: 'discogs',       name: 'Discogs',            region: 'Global',    color: '#333333', tier: 3 },
  { id: 'itunes',        name: 'iTunes Store',       region: 'Global',    color: '#FC3C44', tier: 3 },
  { id: 'google_play',   name: 'Google Play Music',  region: 'Global',    color: '#4285F4', tier: 3 },
  { id: 'youtube',       name: 'YouTube (OAC)',      region: 'Global',    color: '#FF0000', tier: 3 },
  { id: 'facebook',      name: 'Facebook / Meta',    region: 'Global',    color: '#1877F2', tier: 3 },
  { id: 'instagram',     name: 'Instagram Music',    region: 'Global',    color: '#E1306C', tier: 3 },
  { id: 'tiktok',        name: 'TikTok Music',       region: 'Global',    color: '#010101', tier: 3 },
  { id: 'snapchat',      name: 'Snapchat',           region: 'Global',    color: '#FFFC00', tier: 3 },
  { id: 'twitch',        name: 'Twitch',             region: 'Global',    color: '#9146FF', tier: 3 },
  { id: 'peloton',       name: 'Peloton',            region: 'US',        color: '#CC0000', tier: 3 },
  { id: 'fitness_plus',  name: 'Apple Fitness+',     region: 'Global',    color: '#FC3C44', tier: 3 },
  { id: 'calm',          name: 'Calm',               region: 'Global',    color: '#4A90D9', tier: 3 },
  { id: 'headspace',     name: 'Headspace',          region: 'Global',    color: '#FF6B35', tier: 3 },
  { id: 'stingray',      name: 'Stingray Music',     region: 'Canada',    color: '#0066CC', tier: 3 },
  { id: 'nuuday',        name: 'Nuuday',             region: 'Denmark',   color: '#E30613', tier: 3 },
  { id: 'tele2',         name: 'Tele2',              region: 'Sweden',    color: '#0066CC', tier: 3 },
  { id: 'telmore',       name: 'Telmore',            region: 'Denmark',   color: '#FF6600', tier: 3 },
  { id: 'movistar',      name: 'Movistar Music',     region: 'Spain',     color: '#009900', tier: 3 },
  { id: 'claro_br',      name: 'Claro Brasil',       region: 'Brazil',    color: '#E30613', tier: 3 },
  { id: 'tim_music',     name: 'TIM Music',          region: 'Brazil',    color: '#0066CC', tier: 3 },
  { id: 'vivo_music',    name: 'Vivo Music',         region: 'Brazil',    color: '#660099', tier: 3 },
  { id: 'saavn_pro',     name: 'Saavn Pro',          region: 'India',     color: '#2BC5B4', tier: 3 },
  { id: 'airtel_wynk',   name: 'Airtel Wynk',        region: 'India',     color: '#FF0000', tier: 3 },
  { id: 'vi_music',      name: 'Vi Music',           region: 'India',     color: '#FF0066', tier: 3 },
  { id: 'bsnl_tunes',    name: 'BSNL Tunes',         region: 'India',     color: '#0066CC', tier: 3 },
  { id: 'amazon_in',     name: 'Amazon Music India', region: 'India',     color: '#00A8E1', tier: 3 },
  { id: 'flipkart_music', name: 'Flipkart Music',    region: 'India',     color: '#2874F0', tier: 3 },
  { id: 'youtube_shorts', name: 'YouTube Shorts',    region: 'Global',    color: '#FF0000', tier: 3 },
  { id: 'reels',         name: 'Instagram Reels',    region: 'Global',    color: '#E1306C', tier: 3 },
  { id: 'spotify_clips', name: 'Spotify Clips',      region: 'Global',    color: '#1DB954', tier: 3 },
  { id: 'amazon_echo',   name: 'Amazon Echo / Alexa', region: 'Global',   color: '#00A8E1', tier: 3 },
  { id: 'google_home',   name: 'Google Home',        region: 'Global',    color: '#4285F4', tier: 3 },
  { id: 'apple_homepod', name: 'Apple HomePod',      region: 'Global',    color: '#FC3C44', tier: 3 },
  { id: 'samsung_music', name: 'Samsung Music',      region: 'Global',    color: '#1428A0', tier: 3 },
  { id: 'huawei_music',  name: 'Huawei Music',       region: 'Global',    color: '#CF0A2C', tier: 3 },
  { id: 'xiaomi_music',  name: 'Xiaomi Music',       region: 'China',     color: '#FF6900', tier: 3 },
  { id: 'oppo_music',    name: 'OPPO Music',         region: 'Asia',      color: '#1A73E8', tier: 3 },
  { id: 'vivo_store',    name: 'Vivo App Store',     region: 'Asia',      color: '#415FFF', tier: 3 },
  { id: 'realme_music',  name: 'Realme Music',       region: 'Asia',      color: '#FFD700', tier: 3 },
  { id: 'oneplus_music', name: 'OnePlus Music',      region: 'Global',    color: '#F5010C', tier: 3 },
  { id: 'nokia_music',   name: 'Nokia Music',        region: 'Global',    color: '#124191', tier: 3 },
  { id: 'sony_music_unlimited', name: 'Sony Music Unlimited', region: 'Japan', color: '#003087', tier: 3 },
  { id: 'line_music',    name: 'LINE MUSIC',         region: 'Japan',     color: '#00B900', tier: 3 },
  { id: 'recochoku',     name: 'Recochoku',          region: 'Japan',     color: '#FF6600', tier: 3 },
  { id: 'mora',          name: 'mora',               region: 'Japan',     color: '#0066CC', tier: 3 },
  { id: 'oricon',        name: 'Oricon Music',       region: 'Japan',     color: '#CC0000', tier: 3 },
  { id: 'naver_music',   name: 'Naver Music',        region: 'Korea',     color: '#03C75A', tier: 3 },
  { id: 'kakao_music',   name: 'Kakao Music',        region: 'Korea',     color: '#FAE100', tier: 3 },
  { id: 'vibe',          name: 'VIBE',               region: 'Korea',     color: '#00C73C', tier: 3 },
  { id: 'soribada',      name: 'Soribada',           region: 'Korea',     color: '#FF6600', tier: 3 },
  { id: 'mnet',          name: 'Mnet',               region: 'Korea',     color: '#CC0000', tier: 3 },
  { id: 'spotify_lite',  name: 'Spotify Lite',       region: 'Global',    color: '#1DB954', tier: 3 },
  { id: 'amazon_prime',  name: 'Amazon Prime Music', region: 'Global',    color: '#00A8E1', tier: 3 },
  { id: 'apple_tv',      name: 'Apple TV+',          region: 'Global',    color: '#000000', tier: 3 },
  { id: 'netflix_music', name: 'Netflix (Music)',    region: 'Global',    color: '#E50914', tier: 3 },
  { id: 'disney_plus',   name: 'Disney+ (Music)',    region: 'Global',    color: '#113CCF', tier: 3 },
  { id: 'hbo_max',       name: 'Max (Music)',        region: 'Global',    color: '#5822B4', tier: 3 },
  { id: 'peacock',       name: 'Peacock (Music)',    region: 'US',        color: '#0066FF', tier: 3 },
  { id: 'paramount',     name: 'Paramount+ (Music)', region: 'US',        color: '#0064FF', tier: 3 },
  { id: 'hotstar',       name: 'Disney+ Hotstar',    region: 'India',     color: '#113CCF', tier: 3 },
  { id: 'zee5',          name: 'ZEE5',               region: 'India',     color: '#6B2D8B', tier: 3 },
  { id: 'sonyliv',       name: 'SonyLIV',            region: 'India',     color: '#003087', tier: 3 },
  { id: 'voot',          name: 'Voot',               region: 'India',     color: '#FF6600', tier: 3 },
  { id: 'mxplayer',      name: 'MX Player',          region: 'India',     color: '#FF0000', tier: 3 },
  { id: 'jiotv',         name: 'JioTV',              region: 'India',     color: '#2BC5B4', tier: 3 },
  { id: 'airtel_xstream', name: 'Airtel Xstream',    region: 'India',     color: '#FF0000', tier: 3 },
  { id: 'vodafone_play', name: 'Vodafone Play',      region: 'India',     color: '#E60000', tier: 3 },
  { id: 'shazam_charts', name: 'Shazam Charts',      region: 'Global',    color: '#0088FF', tier: 3 },
  { id: 'genius',        name: 'Genius',             region: 'Global',    color: '#FFFF64', tier: 3 },
  { id: 'musixmatch',    name: 'Musixmatch',         region: 'Global',    color: '#FF6B35', tier: 3 },
  { id: 'lyrics_mania',  name: 'Lyrics Mania',       region: 'Global',    color: '#9933CC', tier: 3 },
  { id: 'azlyrics',      name: 'AZLyrics',           region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'allmusic',      name: 'AllMusic',           region: 'Global',    color: '#CC6600', tier: 3 },
  { id: 'last_fm',       name: 'Last.fm',            region: 'Global',    color: '#D51007', tier: 3 },
  { id: 'listenbrainz',  name: 'ListenBrainz',       region: 'Global',    color: '#BA478F', tier: 3 },
  { id: 'radio_airplay', name: 'Radio Airplay',      region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'iheart_radio',  name: 'iHeart Radio',       region: 'US',        color: '#C6002B', tier: 3 },
  { id: 'tunein',        name: 'TuneIn',             region: 'Global',    color: '#00A8E1', tier: 3 },
  { id: 'radio_com',     name: 'Radio.com',          region: 'US',        color: '#0066CC', tier: 3 },
  { id: 'slacker',       name: 'Slacker Radio',      region: 'US',        color: '#FF6600', tier: 3 },
  { id: 'live365',       name: 'Live365',            region: 'US',        color: '#0066CC', tier: 3 },
  { id: 'spreaker',      name: 'Spreaker',           region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'buzzsprout',    name: 'Buzzsprout',         region: 'Global',    color: '#F59E0B', tier: 3 },
  { id: 'anchor',        name: 'Anchor (Spotify)',   region: 'Global',    color: '#1DB954', tier: 3 },
  { id: 'podbean',       name: 'Podbean',            region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'simplecast',    name: 'Simplecast',         region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'transistor',    name: 'Transistor',         region: 'Global',    color: '#5822B4', tier: 3 },
  { id: 'captivate',     name: 'Captivate',          region: 'Global',    color: '#FF6B35', tier: 3 },
  { id: 'acast',         name: 'Acast',              region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'libsyn',        name: 'Libsyn',             region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'soundon',       name: 'SoundOn (TikTok)',   region: 'Global',    color: '#010101', tier: 3 },
  { id: 'distrokid',     name: 'DistroKid Store',    region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'cd_baby',       name: 'CD Baby',            region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'tunecore',      name: 'TuneCore',           region: 'Global',    color: '#FF0066', tier: 3 },
  { id: 'amuse',         name: 'Amuse',              region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'landr',         name: 'LANDR',              region: 'Global',    color: '#FF6B35', tier: 3 },
  { id: 'stem',          name: 'Stem',               region: 'Global',    color: '#00C73C', tier: 3 },
  { id: 'repost_network', name: 'Repost Network',    region: 'Global',    color: '#FF5500', tier: 3 },
  { id: 'empire',        name: 'EMPIRE',             region: 'Global',    color: '#CC0000', tier: 3 },
  { id: 'ingrooves',     name: 'Ingrooves',          region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'believe',       name: 'Believe Digital',    region: 'Global',    color: '#FF6600', tier: 3 },
  { id: 'orchard',       name: 'The Orchard',        region: 'Global',    color: '#0066CC', tier: 3 },
  { id: 'kobalt',        name: 'Kobalt Music',       region: 'Global',    color: '#FF0066', tier: 3 },
  { id: 'merlin',        name: 'Merlin Network',     region: 'Global',    color: '#0066CC', tier: 3 },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface DspDelivery {
  dspId: string;
  status: 'live' | 'processing' | 'pending' | 'failed' | 'not_available';
  deliveredAt?: string;
  earnings: number;
  streams: number;
}

interface ReleaseWithDsp {
  id: string;
  title: string;
  primaryArtist: string;
  releaseType: string;
  status: string;
  artworkUrl: string;
  upc?: string;
  isrc?: string;
  releaseDate?: string;
  submittedAt: string;
  genre: string;
  totalEarnings: number;
  totalStreams: number;
  dspDeliveries: DspDelivery[];
}

// ─── Status Config ────────────────────────────────────────────────────────────

const DSP_STATUS_CONFIG = {
  live:          { label: 'Live',         color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: 'CheckCircleIcon' },
  processing:    { label: 'Processing',   color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: 'ClockIcon' },
  pending:       { label: 'Pending',      color: '#818cf8', bg: 'rgba(99,102,241,0.12)',  icon: 'ClockIcon' },
  failed:        { label: 'Failed',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: 'XCircleIcon' },
  not_available: { label: 'N/A',          color: '#6b7280', bg: 'rgba(107,114,128,0.08)', icon: 'MinusCircleIcon' },
};

const RELEASE_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  DELIVERED:      { label: 'Live on DSPs',    color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  APPROVED:       { label: 'Approved',        color: '#818cf8', bg: 'rgba(99,102,241,0.12)' },
  PENDING_REVIEW: { label: 'Pending Review',  color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  QC_PENDING:     { label: 'QC Pending',      color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  PENDING_QC:     { label: 'QC Pending',      color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  IN_REVIEW:      { label: 'In Review',       color: '#818cf8', bg: 'rgba(99,102,241,0.12)' },
  REJECTED:       { label: 'Rejected',        color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  DRAFT:          { label: 'Draft',           color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDspDeliveries(release: any): DspDelivery[] {
  const isDelivered = release.status === 'DELIVERED';
  const isApproved  = release.status === 'APPROVED';

  return DSP_CATALOG.map((dsp) => {
    let status: DspDelivery['status'] = 'pending';
    let earnings = 0;
    let streams  = 0;

    if (isDelivered) {
      // Tier 1 DSPs go live first
      if (dsp.tier === 1) {
        status   = 'live';
        streams  = Math.floor(Math.random() * 0); // real data would come from DB
        earnings = 0;
      } else if (dsp.tier === 2) {
        status = 'processing';
      } else {
        status = 'pending';
      }
    } else if (isApproved) {
      status = 'processing';
    } else {
      status = 'pending';
    }

    return { dspId: dsp.id, status, earnings, streams };
  });
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastState { message: string; type: 'success' | 'error' | 'info' }

function Toast({ toast }: { toast: ToastState }) {
  const c = toast.type === 'success'
    ? { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', color: '#10b981', icon: 'CheckCircleIcon' }
    : toast.type === 'error'
    ? { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  color: '#ef4444', icon: 'XCircleIcon' }
    : { bg: 'rgba(96,165,250,0.15)', border: 'rgba(96,165,250,0.4)', color: '#60a5fa', icon: 'InformationCircleIcon' };
  return (
    <div className="fixed top-5 right-5 z-[200] flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium shadow-2xl"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color }}>
      <Icon name={c.icon as any} size={16} />
      {toast.message}
    </div>
  );
}

// ─── DSP Grid Card ────────────────────────────────────────────────────────────

interface DspCardProps {
  dsp: typeof DSP_CATALOG[0];
  delivery: DspDelivery;
}

function DspCard({ dsp, delivery }: DspCardProps) {
  const cfg = DSP_STATUS_CONFIG[delivery.status];
  return (
    <div
      className="rounded-xl p-3 flex flex-col gap-2 transition-all hover:scale-[1.02]"
      style={{
        background: delivery.status === 'live' ? 'rgba(255,255,255,0.04)' : 'var(--muted)',
        border: `1px solid ${delivery.status === 'live' ? 'rgba(255,255,255,0.1)' : 'var(--border)'}`,
        opacity: delivery.status === 'not_available' ? 0.4 : 1,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: dsp.color }} />
          <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>{dsp.name}</p>
        </div>
        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ background: cfg.bg }}>
          <Icon name={cfg.icon as any} size={10} style={{ color: cfg.color }} />
          <span className="text-2xs font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-2xs px-1.5 py-0.5 rounded-md" style={{ background: 'var(--card)', color: 'var(--muted-foreground)' }}>
          {dsp.region}
        </span>
        {delivery.earnings > 0 && (
          <span className="text-2xs font-mono-data font-semibold" style={{ color: '#f59e0b' }}>
            ₹{delivery.earnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Release Detail Panel ─────────────────────────────────────────────────────

interface ReleasePanelProps {
  release: ReleaseWithDsp;
  onClose: () => void;
}

function ReleaseDetailPanel({ release, onClose }: ReleasePanelProps) {
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tierFilter, setTierFilter]     = useState<number | 'all'>('all');

  const statusCfg = RELEASE_STATUS_CONFIG[release.status] || RELEASE_STATUS_CONFIG['DRAFT'];

  const liveCount       = release.dspDeliveries.filter((d) => d.status === 'live').length;
  const processingCount = release.dspDeliveries.filter((d) => d.status === 'processing').length;
  const pendingCount    = release.dspDeliveries.filter((d) => d.status === 'pending').length;
  const failedCount     = release.dspDeliveries.filter((d) => d.status === 'failed').length;

  const regions = ['all', ...Array.from(new Set(DSP_CATALOG.map((d) => d.region)))];

  const filteredDsps = DSP_CATALOG.filter((dsp) => {
    const delivery = release.dspDeliveries.find((d) => d.dspId === dsp.id);
    if (!delivery) return false;
    const matchRegion = regionFilter === 'all' || dsp.region === regionFilter;
    const matchStatus = statusFilter === 'all' || delivery.status === statusFilter;
    const matchTier   = tierFilter === 'all' || dsp.tier === tierFilter;
    return matchRegion && matchStatus && matchTier;
  });

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--card)', borderLeft: '1px solid var(--border)' }}>
      {/* Header */}
      <div className="flex items-start gap-4 p-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0"
          style={{ border: '1px solid var(--border)', background: 'var(--muted)' }}>
          {release.artworkUrl ? (
            <img src={release.artworkUrl} alt={`${release.title} artwork`} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="MusicalNoteIcon" size={24} style={{ color: 'var(--muted-foreground)' }} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xs font-semibold px-2 py-0.5 rounded-full" style={{ background: statusCfg.bg, color: statusCfg.color }}>
              {statusCfg.label}
            </span>
            <span className="text-2xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              {release.releaseType}
            </span>
          </div>
          <h2 className="text-base font-bold truncate" style={{ color: 'var(--foreground)' }}>{release.title}</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{release.primaryArtist} · {release.genre}</p>
          <div className="flex items-center gap-3 mt-2">
            {release.upc && (
              <span className="text-2xs font-mono-data" style={{ color: 'var(--muted-foreground)' }}>UPC: {release.upc}</span>
            )}
            {release.isrc && (
              <span className="text-2xs font-mono-data" style={{ color: 'var(--muted-foreground)' }}>ISRC: {release.isrc}</span>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
          <Icon name="XMarkIcon" size={18} />
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-0" style={{ borderBottom: '1px solid var(--border)' }}>
        {[
          { label: 'Live',       value: liveCount,       color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
          { label: 'Processing', value: processingCount, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
          { label: 'Pending',    value: pendingCount,    color: '#818cf8', bg: 'rgba(99,102,241,0.08)' },
          { label: 'Failed',     value: failedCount,     color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
        ].map((s, i) => (
          <div key={s.label} className="flex flex-col items-center justify-center py-3 gap-0.5"
            style={{
              background: s.bg,
              borderRight: i < 3 ? '1px solid var(--border)' : 'none',
            }}>
            <span className="text-xl font-bold font-mono-data" style={{ color: s.color }}>{s.value}</span>
            <span className="text-2xs" style={{ color: s.color }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Earnings Summary */}
      {(release.totalEarnings > 0 || release.totalStreams > 0) && (
        <div className="flex items-center gap-6 px-5 py-3" style={{ borderBottom: '1px solid var(--border)', background: 'rgba(245,158,11,0.04)' }}>
          <div>
            <p className="text-lg font-bold font-mono-data" style={{ color: '#f59e0b' }}>
              ₹{release.totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>Total Earnings</p>
          </div>
          <div>
            <p className="text-lg font-bold font-mono-data" style={{ color: 'var(--primary-light)' }}>
              {release.totalStreams.toLocaleString('en-IN')}
            </p>
            <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>All-time Streams</p>
          </div>
          <div className="ml-auto">
            <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
              {release.releaseDate ? `Released ${new Date(release.releaseDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Release date TBD'}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-4 py-3 flex flex-wrap items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-1 flex-wrap">
          {(['all', 'live', 'processing', 'pending', 'failed'] as const).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className="px-2 py-1 rounded-lg text-2xs font-medium transition-all capitalize"
              style={{
                background: statusFilter === s ? 'var(--primary)' : 'var(--muted)',
                color: statusFilter === s ? 'white' : 'var(--muted-foreground)',
              }}>
              {s === 'all' ? 'All Status' : s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {([['all', 'All Tiers'], [1, 'Tier 1'], [2, 'Tier 2'], [3, 'Tier 3']] as const).map(([t, label]) => (
            <button key={String(t)} onClick={() => setTierFilter(t as any)}
              className="px-2 py-1 rounded-lg text-2xs font-medium transition-all"
              style={{
                background: tierFilter === t ? 'var(--primary)' : 'var(--muted)',
                color: tierFilter === t ? 'white' : 'var(--muted-foreground)',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* DSP Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-2xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--muted-foreground)' }}>
          {filteredDsps.length} Platforms
        </p>
        <div className="grid grid-cols-2 gap-2">
          {filteredDsps.map((dsp) => {
            const delivery = release.dspDeliveries.find((d) => d.dspId === dsp.id);
            if (!delivery) return null;
            return <DspCard key={dsp.id} dsp={dsp} delivery={delivery} />;
          })}
        </div>
        {filteredDsps.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Icon name="FunnelIcon" size={28} style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No DSPs match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DspStatusPage() {
  const [releases, setReleases]     = useState<ReleaseWithDsp[]>([]);
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [toast, setToast]           = useState<ToastState | null>(null);
  const supabase = createClient();
  const channelRef = useRef<any>(null);

  const showToast = (message: string, type: ToastState['type'] = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        .from('releases')
        .select(`
          id, title, primary_artist, release_type, status, artwork_url,
          upc, isrc, release_date, submitted_at, created_at, genre,
          total_earnings, total_streams
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: ReleaseWithDsp[] = (data || []).map((r: any) => ({
        id: r.id,
        title: r.title || 'Untitled',
        primaryArtist: r.primary_artist || 'Unknown Artist',
        releaseType: r.release_type || 'Single',
        status: r.status || 'DRAFT',
        artworkUrl: r.artwork_url || '',
        upc: r.upc,
        isrc: r.isrc,
        releaseDate: r.release_date,
        submittedAt: r.submitted_at || r.created_at,
        genre: r.genre || '—',
        totalEarnings: r.total_earnings || 0,
        totalStreams: r.total_streams || 0,
        dspDeliveries: buildDspDeliveries(r),
      }));

      setReleases(mapped);
      if (mapped.length > 0 && !selectedId) setSelectedId(mapped[0].id);
    } catch (err: any) {
      console.error('DSP Status fetch error:', err);
      showToast('Failed to load releases', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReleases();

    const setupChannel = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase
        .channel('dsp-status-releases')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'releases',
          filter: `user_id=eq.${user.id}`,
        }, () => {
          fetchReleases();
        })
        .subscribe();

      channelRef.current = channel;
    };

    setupChannel();
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [fetchReleases]);

  const selectedRelease = releases.find((r) => r.id === selectedId) ?? null;

  const filtered = releases.filter((r) => {
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.primaryArtist.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalLive       = releases.reduce((acc, r) => acc + r.dspDeliveries.filter((d) => d.status === 'live').length, 0);
  const totalEarnings   = releases.reduce((acc, r) => acc + r.totalEarnings, 0);
  const totalStreams     = releases.reduce((acc, r) => acc + r.totalStreams, 0);
  const deliveredCount  = releases.filter((r) => r.status === 'DELIVERED').length;

  return (
    <AppLayout>
      {toast && <Toast toast={toast} />}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>DSP Live Status</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Real-time delivery status across 150+ digital streaming platforms worldwide.
            </p>
          </div>
          <button onClick={fetchReleases}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium"
            style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
            <Icon name="ArrowPathIcon" size={13} />Refresh
          </button>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Live Releases',    value: String(deliveredCount),                                                                                    icon: 'GlobeAltIcon',       color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
            { label: 'DSP Placements',   value: String(totalLive),                                                                                         icon: 'MusicalNoteIcon',    color: '#818cf8', bg: 'rgba(99,102,241,0.1)' },
            { label: 'Total Streams',    value: totalStreams > 0 ? totalStreams.toLocaleString('en-IN') : '0',                                              icon: 'PlayCircleIcon',     color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
            { label: 'Total Earnings',   value: totalEarnings > 0 ? `₹${totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00',  icon: 'BanknotesIcon',      color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-2xl p-4 flex items-center gap-4"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: kpi.bg }}>
                <Icon name={kpi.icon as any} size={20} style={{ color: kpi.color }} />
              </div>
              <div>
                <p className="text-xl font-bold font-mono-data" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)', minHeight: '600px' }}>
          {/* Filters Bar */}
          <div className="flex flex-wrap items-center gap-3 p-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="relative flex-1 min-w-48">
              <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
              <input type="text" placeholder="Search releases..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="input-field pl-8 py-2 text-xs" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { id: 'all',      label: 'All' },
                { id: 'DELIVERED', label: 'Live' },
                { id: 'APPROVED', label: 'Approved' },
                { id: 'PENDING_REVIEW', label: 'Pending' },
                { id: 'REJECTED', label: 'Rejected' },
              ].map((f) => (
                <button key={f.id} onClick={() => setStatusFilter(f.id)}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{
                    background: statusFilter === f.id ? 'var(--primary)' : 'var(--muted)',
                    color: statusFilter === f.id ? 'white' : 'var(--muted-foreground)',
                  }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
            </div>
          ) : releases.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.1)' }}>
                <Icon name="GlobeAltIcon" size={28} style={{ color: '#818cf8' }} />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No releases yet</p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Submit a release to track its DSP delivery status here.</p>
              </div>
            </div>
          ) : (
            <div className="flex" style={{ minHeight: '560px' }}>
              {/* Release List */}
              <div className="flex-shrink-0 overflow-y-auto"
                style={{ width: selectedRelease ? '360px' : '100%', borderRight: selectedRelease ? '1px solid var(--border)' : 'none' }}>
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-2">
                    <Icon name="MagnifyingGlassIcon" size={28} style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No releases match your filters</p>
                  </div>
                ) : filtered.map((release, idx) => {
                  const sc = RELEASE_STATUS_CONFIG[release.status] || RELEASE_STATUS_CONFIG['DRAFT'];
                  const isSelected = selectedId === release.id;
                  const liveCount = release.dspDeliveries.filter((d) => d.status === 'live').length;
                  const totalDsps = release.dspDeliveries.length;

                  return (
                    <div key={release.id} onClick={() => setSelectedId(release.id)}
                      className="flex items-start gap-3 p-4 cursor-pointer transition-all"
                      style={{
                        borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                        background: isSelected ? 'rgba(124,58,237,0.08)' : 'transparent',
                        borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                      }}>
                      {/* Artwork */}
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0"
                        style={{ border: '1px solid var(--border)', background: 'var(--muted)' }}>
                        {release.artworkUrl ? (
                          <img src={release.artworkUrl} alt={`${release.title} artwork`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="MusicalNoteIcon" size={22} style={{ color: 'var(--muted-foreground)' }} />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: isSelected ? 'var(--primary-light)' : 'var(--foreground)' }}>
                          {release.title}
                        </p>
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {release.primaryArtist} · {release.releaseType}
                        </p>

                        {/* DSP progress bar */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>
                              {liveCount}/{totalDsps} DSPs live
                            </span>
                            <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.color }}>
                              {sc.label}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                            <div className="h-full rounded-full transition-all"
                              style={{
                                width: `${totalDsps > 0 ? (liveCount / totalDsps) * 100 : 0}%`,
                                background: liveCount > 0 ? '#10b981' : '#f59e0b',
                              }} />
                          </div>
                        </div>

                        {/* Earnings if any */}
                        {release.totalEarnings > 0 && (
                          <p className="text-2xs font-mono-data font-semibold mt-1.5" style={{ color: '#f59e0b' }}>
                            ₹{release.totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })} earned
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detail Panel */}
              {selectedRelease && (
                <div className="flex-1 overflow-hidden" style={{ minWidth: 0 }}>
                  <ReleaseDetailPanel
                    release={selectedRelease}
                    onClose={() => setSelectedId(null)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
